import { RECENT_DATE_RANGE } from '@bitstopco/bitstop-theme/constants';

import type { ApiListingFilter, ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

import { endOfDay, isValid, startOfDay } from 'date-fns';
import format from 'date-fns/format';
import { v4 as uuid } from 'uuid';

import type { RootState } from '@/store';
import { store } from '@/store';
import { customerApi } from '@/store/api/services/common/customer';
import { issuesApi } from '@/store/api/services/issues/issues';
import { locationsApi } from '@/store/api/services/locations/locations';
import { machineApi } from '@/store/api/services/machines/machine';
import { machinesApi } from '@/store/api/services/machines/machines';

import { DEFAULT_PAGINATION } from '@/constants/api';
import { DATE_FORMATS } from '@/constants/app/date';

import type {
  ApiFilterValidationConfigType,
  ApiListingLegacyRequest,
  ApiMetaResponse,
} from '@/types';

const FILTER_VALIDATION_CONFIG: ApiFilterValidationConfigType = {
  customer: {
    label: 'Customer',
    action: customerApi.endpoints.getV1Customer,
  },
  issue: {
    label: 'Issue',
    action: issuesApi.endpoints.getIssues,
  },
  machine: {
    label: 'Machine',
    action: machineApi.endpoints.getMachine,
  },
  location: {
    label: 'Location',
    action: locationsApi.endpoints.getLocations,
  },
  pick_up_id: {
    label: 'Pick up ID',
    action: machinesApi.endpoints.getMachines,
  },
};

const getDate = (value: string) => format(new Date(value), DATE_FORMATS.simpleVariant);
const getDateStartEnd = (value: string) =>
  format(new Date(value), `${DATE_FORMATS.simpleVariant} HH:mm:ss`);

/**
 * Get Legacy pagination response and transform it to V2 response (without headers manipulation)
 *
 * @template T
 * @param {T[]} data
 * @param {number} [page=1]
 * @param {number} [limit=10]
 * @param {number} [totalItems=0]
 * @return {*}
 */
export const getLegacyPaginatedResponse = <T = any>(
  data: T[],
  page = 1,
  limit = 10,
  totalItems = 0,
) => {
  return {
    data,
    correlation_id: uuid(),
    meta: {
      filters: [],
      pagination: {
        items_per_page: limit,
        current_page: page,
        total_items: totalItems,
        total_pages: Math.ceil(totalItems / limit),
      },
      sort_by: 'id',
      sort_ascending: true,
    },
  } as ApiResponse<T[]>;
};

/**
 * Get date range value from listing filter payload
 *
 * @param {ApiListingFilter} [filter]
 * @return {*}
 */
export const getDateRangeValueFromListingFilter = (filter?: ApiListingFilter) => {
  try {
    const startDate = startOfDay(
      new Date(
        filter?.operands?.find(
          ({ property, operator }) => property === 'date' && operator === 'greater_than_or_equal',
        )?.value,
      ),
    );

    const endDate = endOfDay(
      new Date(
        filter?.operands?.find(
          ({ property, operator }) => property === 'date' && operator === 'less_than_or_equal',
        )?.value,
      ),
    );

    if (isValid(startDate) && isValid(endDate)) {
      return { startDate, endDate };
    }
  } catch {}

  return null;
};

/**
 * Get filter value from listing filter payload
 *
 * @param {string} id
 * @param {ApiListingFilter} [filter]
 * @return {*}
 */
export const getValueFromListingFilter = (
  id: string,
  filter?: ApiListingFilter,
): null | string | number => {
  if (!filter) {
    return null;
  }

  if (filter?.property === id) {
    return filter.value;
  }

  return filter?.operands?.find?.(({ property }) => property === id)?.value;
};

/**
 * Get Legacy paginated payload and transform it to V2 payload
 *
 * @param {ApiListingRequest} payload
 * @return {ApiListingLegacyRequest}
 */
export const getLegacyPaginatedPayload = (
  { search_text, pagination, filter }: ApiListingRequest,
  extraParamsIds?: string[],
): ApiListingLegacyRequest & Record<string, any> => {
  const dateRange = getDateRangeValueFromListingFilter(filter);

  const page = pagination?.current_page;
  const items_per_page = pagination?.items_per_page;

  const rangeFilter =
    dateRange?.startDate && dateRange?.endDate
      ? {
          start: getDateStartEnd(dateRange?.startDate.toString()),
          range_start: getDate(dateRange?.startDate.toString()),
          range_end: getDate(dateRange?.endDate.toString()),
          end: getDateStartEnd(dateRange?.endDate.toString()),
        }
      : {};

  const extraParams =
    extraParamsIds?.reduce((extraParams, id) => {
      const newValue = getValueFromListingFilter(id, filter);
      if (newValue != null) {
        extraParams[id] = newValue;
      }

      return extraParams;
    }, {}) || {};

  return {
    page,
    limit: items_per_page,
    items_per_page,
    ...rangeFilter,
    ...extraParams,
    ...(search_text && { query: search_text }),
    ...(search_text && { search_text }),
  };
};

/**
 * Get Legacy pagination response and transform it to V2 response (including headers manipulation)
 *
 * @template T
 * @param {T[]} data
 * @param {ApiMetaResponse} meta
 * @param {FetchRequest} payload
 * @return {*}
 */

export const transformLegacyPaginationResponse = <T>(
  data: T[],
  meta: ApiMetaResponse,
  payload: ApiListingRequest,
) => {
  const totalItems = Number(meta?.headers?.['x-total-count'] || 0);
  return getLegacyPaginatedResponse<T>(
    data,
    payload?.pagination?.current_page,
    +(payload?.pagination?.items_per_page || 1),
    totalItems,
  );
};

/**
 * Get last RTK-Query endpoint data by his request ID
 * * This helper is very useful to get the last request data (including from cache) if we have the requestID
 *
 * @param {RootState} state
 * @param {string} requestId
 * @return {*}
 */
export const getLastEndpointDataByRequestId = (state: RootState, requestId: string) => {
  const found = Object.keys(state.api.queries).filter((key) => {
    const current = state.api.queries[key];

    return current?.requestId === requestId;
  })[0];

  if (found) {
    return state.api.queries[found];
  }
};

/**
 * Append a new Array of filters to the filters object.
 * - If the filters object has no filters, this function sets the new filter as the plain filter object.
 * - If the filters object has a plain filter, this function converts it to an `operands` array and appends the new filter.
 * - If the filters object already has multiple filters (in the `operands` array), this function appends the new filter to the `operands`.
 *
 * @param {ApiListingRequest} filters - The main object that holds the filters
 * @param {Record<string, any>} newFilter - The new filter object Array to append to the filters object
 * @returns {ApiListingRequest} - The updated filters object with the appended filter
 */
export const appendToFilters = (request: ApiListingRequest, newFilters: Record<string, any>[]) => {
  // No new filters → just return request unchanged
  if (!Array.isArray(newFilters) || newFilters.length === 0) {
    return request;
  }
  const filter = request.filter;

  // No existing filter
  if (!filter) {
    return {
      ...request,
      filter:
        newFilters.length === 1 ? newFilters[0] : { operator: 'and', operands: [...newFilters] },
    };
  }

  // Existing is an AND group with operands → append
  if (filter.operator === 'and' && Array.isArray(filter.operands)) {
    return {
      ...request,
      filter: { ...filter, operands: [...filter.operands, ...newFilters] },
    };
  }

  // Otherwise wrap old filter + new ones in an AND group
  return {
    ...request,
    filter: { operator: 'and', operands: [filter, ...newFilters] },
  };
};

/**
 * Walk a filter tree (leaf, AND/OR wrappers, nested) and coerce string boolean
 * values ('true'/'false') to JSON booleans for the listed properties. Mutates in place.
 */
export const coerceBooleanFilterValues = (
  filters: Record<string, any> | null | undefined,
  properties: string[],
) => {
  if (filters === null || filters === undefined || typeof filters !== 'object') return;
  if (Array.isArray(filters)) {
    for (const item of filters) coerceBooleanFilterValues(item, properties);
    return;
  }
  if (
    typeof filters.property === 'string' &&
    properties.includes(filters.property) &&
    typeof filters.value === 'string'
  ) {
    filters.value = filters.value === 'true';
  }
  for (const key in filters) {
    if (filters[key] !== null && filters[key] !== undefined && typeof filters[key] === 'object') {
      coerceBooleanFilterValues(filters[key], properties);
    }
  }
};

/**
 * Transform the cases base filters to a valid backend filter
 * Converts string boolean values ('true'/'false') to actual boolean values for boolean properties
 *
 * @param {Record<string, any>} filters
 */
export const transformCasesFilterPayload = (filters: Record<string, any> | null | undefined) => {
  if (filters === null || filters === undefined) {
    return;
  }

  if (typeof filters === 'object') {
    if (Array.isArray(filters)) {
      for (let i = 0; i < filters.length; i++) {
        transformCasesFilterPayload(filters[i]);
      }
    } else {
      for (const key in filters) {
        if (
          key === 'property' &&
          filters[key] === 'auto_created' &&
          typeof filters.value === 'string'
        ) {
          // Convert string boolean values to actual booleans
          filters.value = filters.value === 'true';
        } else if (filters[key] !== null && filters[key] !== undefined) {
          transformCasesFilterPayload(filters[key]);
        }
      }
    }
  }
};

/**
 * Transform the machines base filters to a valid backend filter.
 *
 * MACHINES_FILTERS_CONFIG can't express "location_id is/isn't null" directly: the theme's
 * useTable drops any filter whose selected value is falsy before it ever reaches the request
 * (see getMappedFilters), and null is falsy, so a select option can never carry a literal null
 * through that pipeline. The "Location" filter therefore uses a synthetic `installed` property
 * with truthy string options, and this rewrites it into the real location_id null-check the
 * backend can execute.
 *
 * @param {Record<string, any>} filters
 */
export const transformMachinesFilterPayload = (filters: Record<string, any> | null | undefined) => {
  if (filters === null || filters === undefined) {
    return;
  }

  if (typeof filters === 'object') {
    if (Array.isArray(filters)) {
      for (let i = 0; i < filters.length; i++) {
        transformMachinesFilterPayload(filters[i]);
      }
    } else if (filters.property === 'installed') {
      filters.property = 'location_id';
      filters.operator = filters.value === 'installed' ? 'not_equal' : 'equal';
      filters.value = null;
    } else {
      for (const key in filters) {
        if (
          filters[key] !== null &&
          filters[key] !== undefined &&
          typeof filters[key] === 'object'
        ) {
          transformMachinesFilterPayload(filters[key]);
        }
      }
    }
  }
};

/**
 * Validate id handler used in some Table filters
 * ! See FILTER_VALIDATION_CONFIG configuration if you want to add more validations
 */
export const validateFilterById =
  (id: string) => async (value: ApiListingRequest | string | number) => {
    const { label, action } = FILTER_VALIDATION_CONFIG[id as keyof typeof FILTER_VALIDATION_CONFIG];

    try {
      const response = await store.dispatch(action.initiate(value)).unwrap();

      if (Array.isArray(response?.data) && !response?.data?.length) {
        throw new Error();
      }

      return null;
    } catch {
      let searchIdValue = value;
      /**
       * In Locations view, when filtering by ID, I must force id
       * value in order for it to appear in error text.
       */
      // Ensure value is an object before accessing its properties
      if ((id === 'location' || id === 'issue') && typeof value === 'object' && 'filter' in value) {
        searchIdValue = value.filter?.operands?.[0]?.value ?? 'Unknown';
        return `• ${label} with ID ${searchIdValue} not found.`;
      }
      return `• ${label} with ID ${value} not found.`;
    }
  };
/**
 * Get a valid filters for a specific date range
 ** By default the startDate and endDate will be set for the recent 30 days
 *
 * @param {string} [property='created_at']
 * @param {*} [startDate=RECENT_DATE_RANGE.startDate?.toISOString()]
 * @param {*} [endDate=RECENT_DATE_RANGE.endDate?.toISOString()]
 */
export const getDateRangeFilter = (
  property = 'created_at',
  startDate = RECENT_DATE_RANGE.startDate?.toISOString(),
  endDate = RECENT_DATE_RANGE.endDate?.toISOString(),
) => ({
  filter: {
    operator: 'and',
    operands: [
      {
        property,
        operator: 'greater_than_or_equal',
        value: startDate,
      },
      {
        property,
        operator: 'less_than_or_equal',
        value: endDate,
      },
    ],
  },
});

/**
 * Locally paginate the data to simulate the endpoint pagination (for endpoints with missing pagination)
 *
 * @template T
 * @param {T[]} data
 * @param {ApiListingRequest} payload
 * @return {*}
 */
export const locallyPaginatedData = <T>(data: T[], payload?: ApiListingRequest) => {
  const { current_page, items_per_page } = payload?.pagination || DEFAULT_PAGINATION;

  const start = (current_page - 1) * items_per_page;
  const end = start + items_per_page;
  const paginatedData = data.slice(start, end);
  const totalPages = Math.ceil(data.length / items_per_page);

  const pagination = {
    total_items: data.length,
    total_pages: totalPages,
    current_page,
    items_per_page,
  };

  const sortBy = payload?.pagination?.sort_by;
  const sortAscending = payload?.pagination?.sort_ascending;
  if (sortBy) {
    paginatedData.sort((a, b) => {
      const valA = a[sortBy];
      const valB = b[sortBy];

      // Determine the sort order
      const sortOrder = sortAscending ? 1 : -1;

      // Check if the property is a number
      if (typeof valA === 'number' && typeof valB === 'number') {
        return (valA - valB) * sortOrder;
      }

      // Check if the property is a string
      if (typeof valA === 'string' && typeof valB === 'string') {
        return valA.localeCompare(valB) * sortOrder;
      }

      // Fallback for other types or mixed types
      return 0;
    });
  }

  return {
    data: paginatedData,
    meta: {
      pagination,
    },
  };
};
